<template>
  <app-page
    :title="`Batch #${batch?.batch_number || ''}`"
    subtitle="View details about this batch"
    icon="rotate"
    back-route="/one-time-payments"
  >
    <div v-if="batch">
      <div class="batch-details">
        <p><strong>Status:</strong> {{ getStatus(batch.status) }}</p>
        <p><strong>Created at:</strong> {{ format(new Date(batch.created_at), 'do MMMM yyyy HH:mm a') }}</p>
        <p v-if="batch.submitted_at">
          <strong>Submitted at:</strong>
          {{ batch.submitted_at ? format(new Date(batch.submitted_at), 'do MMMM yyyy HH:mm a') : 'Not yet submitted' }}
        </p>
        <div class="mt-3">
          <h3>Payments</h3>
          <div v-if="batch.payments.length !== 0">
            <b-table
              class="payments"
              :items="batch.payments"
              :fields="FIELDS"
              responsive="sm"
              :tbody-tr-class="rowClass"
            >
              <template v-slot:cell(payment_type)="data">
                <PaymentTypePill :reason="data.item.payment_type" :reason-options="paymentReasons" />
              </template>
              <template v-slot:cell(view_tracking)="data">
                <div @click="toggleDetails(data.item)">
                  <b-button variant="link" class="p-0" v-if="!data.item._showDetails">Show tracking</b-button>
                  <b-button variant="link" class="p-0" v-else>Hide tracking</b-button>
                </div>
              </template>
              <template v-slot:cell(retry)="data">
                <div
                  class="go-to"
                  :id="`retry-batch-${data?.item?.batch}`"
                  v-if="data.item.status === 'FAILED'"
                  v-b-modal="`retry-batch-${data?.item?.batch}`"
                >
                  <Refresh />
                  <b-tooltip :target="`retry-batch-${data?.item?.batch}`" triggers="hover">
                    Retry this payment
                  </b-tooltip>
                </div>
                <b-modal
                  :id="`retry-batch-${data.item.batch}`"
                  size="lg"
                  title="Retry payment?"
                  :ok-title="showCutOffTimeWarning ? 'Yes, send by Nextday ACH' : 'Yes, send by Sameday ACH'"
                  ok-variant="danger"
                  centered
                  @ok="retryPayment(data.item)"
                >
                  <div v-if="showCutOffTimeWarning">
                    <p>
                      Payment will be sent via Nextday ACH and the money will be available in the employee's account on
                      the next business day.
                    </p>
                  </div>
                  <div v-else>
                    <p>
                      Payment will be sent via Sameday ACH and the money will be available in the employee's account by
                      end of day today.
                    </p>
                  </div>

                  <p>Do you want to continue?</p>
                </b-modal>
              </template>
              <template v-slot:row-details="row">
                <div class="payment-tracking-table-wrapper">
                  <PaymentTrackingTable
                    :paymentId="row.item.off_cycle_payment_id"
                    :visible="!!row.item.off_cycle_payment_id && row.item._showDetails"
                  />
                </div>
              </template>
            </b-table>
          </div>
          <div v-else>No payments in this batch</div>
        </div>
      </div>
    </div>
    <div v-else>Loading...</div>
  </app-page>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import AppPage from '@/components/AppPage.vue';
import { useRoute } from 'vue-router/composables';
import { toCurrency } from '@/utils';
import PaymentTypePill from '@/pages/one-time-payments/components/payment-type-pill.vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { getOffCycleConfig, getOffCyclePaymentsByBatchId, retryOffCyclePayment } from '@/queries/one-time-payments';
import PaymentTrackingTable from '@/pages/one-time-payments/components/payment-tracking-table.vue';
import Refresh from '@/assets/icons/Refresh.vue';
import { format } from 'date-fns';

const batch = ref(null);
const route = useRoute();
const batchId = ref(route.params.batchId);
const paymentReasons = ref([]);

const activeBatchRow = ref({
  index: null,
  open: false,
});

const getPaymentBatchQuery = useQuery(getOffCyclePaymentsByBatchId(batchId));
const offCycleConfig = useQuery(getOffCycleConfig());
const retryPaymentMutation = useMutation(retryOffCyclePayment());
const hasOnlyOnePayment = computed(() => batch?.value?.payments?.length === 1);

const showCutOffTimeWarning = computed(() => {
  const cutOffTime = offCycleConfig.data.value?.ach?.sameday?.cut_off_time_utc;
  if (!cutOffTime) {
    return false;
  }
  return new Date(cutOffTime) < new Date();
});

const toggleDetails = (item, index) => {
  item._showDetails = !item._showDetails;

  activeBatchRow.value = {
    off_cycle_payment_id: item.off_cycle_payment_id,
    index,
    open: activeBatchRow.value.open,
  };
};

const rowClass = item => {
  const trClasses = ['tr-card'];
  if (!item) {
    return trClasses;
  }
  if (item.off_cycle_payment_id === activeBatchRow.value.off_cycle_payment_id) {
    trClasses.push('selected-row');
  }

  if (item.off_cycle_payment_id === activeBatchRow.value.off_cycle_payment_id && item._showDetails) {
    trClasses.push('open');
  }
  return trClasses;
};

const getStatus = status => {
  if (status === 'PROCESSED') {
    return 'PAYMENT MADE';
  }

  if (status === 'COMPLETE') {
    return 'PAYMENT MADE';
  }

  if (status === 'FAILED') {
    return 'PAYMENT FAILED';
  }

  if (status === 'PENDING') {
    return 'PAYMENT SENT';
  }

  return status;
};

watch([getPaymentBatchQuery.fetchStatus, getPaymentBatchQuery.isPending], () => {
  if (getPaymentBatchQuery.data.value) {
    batch.value = getPaymentBatchQuery.data.value;

    if (batch.value.payments.length === 1) {
      batch.value.payments[0]._showDetails = true;
    } else {
      batch.value.payments.forEach(payment => {
        payment._showDetails = false;
      });
    }
  }
});

watch([offCycleConfig.fetchStatus, offCycleConfig.isPending], () => {
  if (offCycleConfig.data.value) {
    paymentReasons.value = offCycleConfig.data.value.supported_payment_types;
  }
});

const RAW_FIELDS = [
  { key: 'employee_code', label: 'Employee Code' },
  { key: 'employee_email', label: 'Email' },
  {
    key: 'transaction_properties.to_account',
    label: 'Account number',
    formatter: (value, col, row) =>
      value
        ? '****' + value?.slice(-4)
        : '****' + row?.account_number?.slice(-4) || '****' + row?.beneficiary_account_number?.slice(-4),
  },
  {
    key: 'transaction_properties.to_routing',
    label: 'Routing number',
    formatter: (value, col, row) => (value ? value : row?.routing_number || row?.beneficiary_routing_number),
  },
  { key: 'transaction_properties.pay_type', label: 'Payment method' },
  {
    key: 'payment_rail_used',
    label: 'Status',
    formatter: value => (value ? 'PAYMENT MADE' : ''),
  },
  { key: 'payment_type', label: 'Reason' },
  { key: 'amount', label: 'Amount', formatter: value => toCurrency(value, 'USD') },

  {
    key: 'retry',
    label: 'Retry',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
  { key: 'view_tracking', label: '', hide: hasOnlyOnePayment },
];

const FIELDS = computed(() => {
  return RAW_FIELDS.filter(field => !field.hide);
});

const retryPayment = payment => {
  const payload = {
    off_cycle_payment_id: payment.off_cycle_payment_id,
  };

  if (showCutOffTimeWarning.value) {
    payload.force_next_day_ach = true;
  }

  retryPaymentMutation.mutate(payload);
};
</script>

<style scoped>
.payment-tracking-table-wrapper {
  display: flex;
  justify-content: center;
}
</style>
